import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { matchesQuery, parseUserTags } from './searchMatch'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
`

const StyledLabel = styled.label`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  color: #364153;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`

const Required = styled.span`
  color: #DC2626;
  margin-right: 2px;
`

const StyledInput = styled.input<{ $invalid?: boolean }>`
  display: flex;
  height: 41.804px;
  padding: 8px 16px;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid ${({ $invalid }) => ($invalid ? '#DC2626' : '#D1D5DC')};
  color: rgba(15, 23, 42, 0.50);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  outline: none;
  width: 100%;
  box-sizing: border-box;
`

const StyledTextarea = styled.textarea<{ $invalid?: boolean }>`
  display: flex;
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid ${({ $invalid }) => ($invalid ? '#DC2626' : '#D1D5DC')};
  color: rgba(15, 23, 42, 0.70);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.6;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  /* @noflip */ direction: rtl;
`

const StyledSelect = styled.select<{ $invalid?: boolean }>`
  display: flex;
  height: 41.804px;
  padding: 8px 16px;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid ${({ $invalid }) => ($invalid ? '#DC2626' : '#D1D5DC')};
  color: rgba(15, 23, 42, 0.50);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
`

const ColorList = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const ColorButton = styled.button<{ bg: string; selected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ bg }) => bg};
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  box-shadow: ${({ selected }) =>
    selected ? '0 0 0 2px #FFF, 0 0 0 4px #101828' : 'none'};
`

type ColorPickerProps = {
  label: string
  required?: boolean
  value: string
  onChange: (color: string) => void
  colors: string[]
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ label, required = false, value, onChange, colors }) => (
  <Wrapper>
    <StyledLabel>
      {label}
      {required && <Required>*</Required>}
    </StyledLabel>
    <ColorList>
      {colors.map((color, i) => (
        <ColorButton
          key={`${color}-${i}`}
          bg={color}
          selected={value === color}
          onClick={() => onChange(color)}
          type="button"
        />
      ))}
    </ColorList>
  </Wrapper>
)

type InputProps = {
  label: string
  required?: boolean
  // A required field the form asked for and did not get: the border turns red
  // until something is typed into it.
  invalid?: boolean
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
}

type SelectProps = {
  label: string
  required?: boolean
  invalid?: boolean
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  disabled?: boolean
}

export const FormInput: React.FC<InputProps> = ({
  label,
  required = false,
  invalid = false,
  value,
  onChange,
  placeholder = '',
  disabled = false,
}) => (
  <Wrapper>
    <StyledLabel>
      {label}{required && <Required>*</Required>}
    </StyledLabel>
    <StyledInput
      $invalid={invalid}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
  </Wrapper>
)

// Multi-line variant of FormInput: keeps line breaks, and matches the look of
// the protocol content field in the edit-protocol view.
export const FormTextarea: React.FC<InputProps & { rows?: number }> = ({
  label,
  required = false,
  invalid = false,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  rows = 4,
}) => (
  <Wrapper>
    <StyledLabel>
      {label}{required && <Required>*</Required>}
    </StyledLabel>
    <StyledTextarea
      $invalid={invalid}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      dir="rtl"
    />
  </Wrapper>
)

export const FormSelect: React.FC<SelectProps> = ({
  label,
  required = false,
  invalid = false,
  value,
  onChange,
  options,
  disabled = false,
}) => (
  <Wrapper>
    <StyledLabel>
      {label}{required && <Required>*</Required>}
    </StyledLabel>
    <StyledSelect
      $invalid={invalid}
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </StyledSelect>
  </Wrapper>
)

const SearchWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
  position: relative;
`

const SelectedTags = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  align-self: stretch;
`

const Tag = styled.div<{ tagColor?: string; tagBg?: string }>`
  display: flex;
  padding: 4px 12px 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 3.991px;
  border-radius: 30px;
  background: ${({ tagBg }) => tagBg || '#DBEAFE'};
  color: ${({ tagColor }) => tagColor || '#1447E6'};
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

const TagRemove = styled.button<{ tagColor?: string; }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${({ tagColor }) => tagColor || '#1447E6'};
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 1;
`

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 10;
  background: #fff;
  border: 0.909px solid #D1D5DC;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
`
const DropdownEmpty = styled.div`
  padding: 10px 16px;
  font-family: Rubik;
  font-size: 14px;
  color: #64748B;
  text-align: center;
`
const DropdownItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  font-family: Rubik;
  font-size: 14px;
  color: #0F172A;
  text-align: right;
  &:hover {
    background: #EFF6FF;
  }
`

export type UserOption = {
  userId: number
  fullName: string
  tags?: string[] | string
}

type FormSearchProps = {
  label: string
  required?: boolean
  value: UserOption[]
  onChange: (value: UserOption[]) => void
  options: UserOption[]
  placeholder?: string
  tagColor?: string
  tagBg?: string
}

// `label` is what the field is called; pass an empty one where the surrounding
// heading already names it. The required mark rides with the label, so a
// required picker has to carry one.
export const FormSearch: React.FC<FormSearchProps> = ({
  label,
  required = false,
  value,
  onChange,
  options,
  placeholder,
  tagColor,
  tagBg,
}) => {
  const { t } = useTranslation('protocols')
  const [search, setSearch] = useState('')

  // A user is a match on their name or on any one of their tags. Both go into
  // the same haystack, so "inna manager" and "manager inna" find the same
  // person, and part of a tag is enough where a whole tag used to be needed.
  const filtered = options
    .filter(o => !value.some(v => v.userId === o.userId)
      && matchesQuery(search, [o.fullName, ...parseUserTags(o.tags)]))
    .slice(0, 5)

  const addItem = (item: UserOption) => {
    onChange([...value, item])
    setSearch('')
  }

  const removeItem = (userId: number) => {
    onChange(value.filter(v => v.userId !== userId))
  }

  return (
    <SearchWrapper>
      {!!label && (
        <StyledLabel>
          {label}{required && <Required>*</Required>}
        </StyledLabel>
      )}
      {value.length > 0 && (
        <SelectedTags>
          {value.map(item => (
            <Tag key={item.userId} tagColor={tagColor} tagBg={tagBg}>
              {item.fullName}
              <TagRemove type="button" tagColor={tagColor} onClick={() => removeItem(item.userId)}>×</TagRemove>
            </Tag>
          ))}
        </SelectedTags>
      )}
      <StyledInput
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={placeholder ?? t`search_generic`}
      />
      {search.trim() && (
        <DropdownList>
          {filtered.length > 0
            ? filtered.map(item => (
              <DropdownItem key={item.userId} onMouseDown={e => e.preventDefault()} onClick={() => addItem(item)}>
                {item.fullName}
              </DropdownItem>
            ))
            : <DropdownEmpty>{t`search_no_results`}</DropdownEmpty>}
        </DropdownList>
      )}
    </SearchWrapper>
  )
}

type FormTagSearchProps = {
  label?: string
  required?: boolean
  value: string[]
  onChange: (value: string[]) => void
  options: string[]
  placeholder?: string
  tagColor?: string
  tagBg?: string
}

export const FormTagSearch: React.FC<FormTagSearchProps> = ({
  label = '',
  required = false,
  value,
  onChange,
  options,
  placeholder,
  tagColor,
  tagBg,
}) => {
  const { t } = useTranslation('protocols')
  const [search, setSearch] = useState('')

  const filtered = options
    .filter(tag => !value.includes(tag) && matchesQuery(search, [tag]))
    .slice(0, 5)

  const addTag = (tag: string) => {
    onChange([...value, tag])
    setSearch('')
  }

  const removeTag = (tag: string) => {
    onChange(value.filter(t => t !== tag))
  }

  return (
    <SearchWrapper>
      {!!label && (
        <StyledLabel>
          {label}{required && <Required>*</Required>}
        </StyledLabel>
      )}
      {value.length > 0 && (
        <SelectedTags>
          {value.map(tag => (
            <Tag key={tag} tagColor={tagColor} tagBg={tagBg}>
              {tag}
              <TagRemove type="button" tagColor={tagColor} onClick={() => removeTag(tag)}>×</TagRemove>
            </Tag>
          ))}
        </SelectedTags>
      )}
      <StyledInput
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={placeholder ?? t`search_tag_to_add`}
      />
      {search.trim() && (
        <DropdownList>
          {filtered.length > 0
            ? filtered.map(tag => (
              <DropdownItem key={tag} onMouseDown={e => e.preventDefault()} onClick={() => addTag(tag)}>
                {tag}
              </DropdownItem>
            ))
            : <DropdownEmpty>{t`search_no_results`}</DropdownEmpty>}
        </DropdownList>
      )}
    </SearchWrapper>
  )
}
